import { adminRoutePaths } from "@pages/AdminDashboard/adminRoutes";
import { shortenIdentifier } from "@pages/AdminDashboard/ui/DataTable/cells";

// The row of Case Search or Order Search an account was opened from (FIND-R26, ACCT-R16).
export type SearchOrigin =
  | { search: "case"; caseNumber: number }
  | { search: "order"; orderNumber: number; sharedOrderId: string };

interface AccountFromSearchState {
  openedFromSearch: SearchOrigin;
}

export interface AccountFromSearchLocation {
  pathname: string;
  search: string;
  state: AccountFromSearchState;
}

// The User tab at the address of the User's email (SHELL-R17); the row it came from travels with
// the address, so the address itself is the one the global search opens.
export const accountFromSearchLocation = (
  email: string,
  origin: SearchOrigin
): AccountFromSearchLocation => ({
  pathname: adminRoutePaths.find,
  search: `?search=user&q=${encodeURIComponent(email)}`,
  state: { openedFromSearch: origin },
});

const isSearchOrigin = (value: unknown): value is SearchOrigin => {
  if (typeof value !== "object" || value === null) return false;

  const origin = value as Partial<Record<string, unknown>>;
  if (origin.search === "case") return typeof origin.caseNumber === "number";
  if (origin.search === "order")
    return typeof origin.orderNumber === "number" && typeof origin.sharedOrderId === "string";

  return false;
};

// The row an account was opened from, or null for an account reached any other way.
export const readSearchOrigin = (locationState: unknown): SearchOrigin | null => {
  if (typeof locationState !== "object" || locationState === null) return null;

  const { openedFromSearch } = locationState as Partial<AccountFromSearchState>;

  return isSearchOrigin(openedFromSearch) ? openedFromSearch : null;
};

export interface SearchOriginDescription {
  source: string;
  picked: "Case" | "Order";
  backLabel: string;
  backAddress: string;
}

// The words of the bar on top of an account opened from a search (ACCT-R17) and where its Back
// Button returns (ACCT-R18).
export const describeSearchOrigin = (origin: SearchOrigin): SearchOriginDescription => {
  if (origin.search === "case")
    return {
      source: `Case Search · Case #${origin.caseNumber}`,
      picked: "Case",
      backLabel: "← Back to Case Search",
      backAddress: `${adminRoutePaths.find}?search=case`,
    };

  return {
    source: `Order Search · ${shortenIdentifier(origin.sharedOrderId)}`,
    picked: "Order",
    backLabel: "← Back to Order Search",
    backAddress: `${adminRoutePaths.find}?search=order`,
  };
};
